/**
 * Collection Navigation
 * Sidebar navigation for content collections
 */

import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
	Search,
	Database,
	Folder,
	FolderOpen,
	ChevronRight,
	Eye,
	EyeOff,
	Bookmark,
	MoreVertical,
	Pencil,
	Trash2,
} from 'lucide-react';
import { useContentStore } from '@/stores/content';
import { usePresetsStore } from '@/stores/presets';
import { useAuthStore } from '@/stores/auth';
import { toast } from 'sonner';
import type { Collection } from '@/hooks/useCollections';
import type { Preset } from '@/types/presets';

interface CollectionNavigationProps {
	collections: Collection[];
	currentCollection?: string;
	activeBookmarkId?: number | null;
}

interface CollectionItemProps {
	collection: Collection;
	isActive: boolean;
	search: string;
	showHidden: boolean;
	level?: number;
	children?: Collection[];
	bookmarks?: Preset[];
	activeBookmarkId?: number | null;
	childrenMap?: Map<string, Collection[]>;
	bookmarksMap?: Map<string, Preset[]>;
	currentCollection?: string;
}

interface NavigationBookmarkProps {
	bookmark: Preset;
	isActive: boolean;
	level: number;
}

function NavigationBookmark({ bookmark, isActive, level }: NavigationBookmarkProps) {
	const navigate = useNavigate();
	const { savePreset, delete: deletePreset } = usePresetsStore();
	const { user } = useAuthStore();
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [editName, setEditName] = useState(bookmark.bookmark || '');
	const [editIcon, setEditIcon] = useState(bookmark.icon || 'bookmark');
	const [editColor, setEditColor] = useState(bookmark.color || '');
	const [saving, setSaving] = useState(false);

	const color = bookmark.color;
	const isOwner = bookmark.user === user?.id;

	const handleEdit = async () => {
		if (!editName.trim()) return;

		setSaving(true);
		try {
			await savePreset({
				...bookmark,
				bookmark: editName.trim(),
				icon: editIcon,
				color: editColor || null,
			});
			toast.success('Bookmark updated');
			setEditDialogOpen(false);
		} catch (error) {
			toast.error('Failed to update bookmark');
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		setSaving(true);
		try {
			await deletePreset([bookmark.id!]);
			toast.success('Bookmark deleted');
			setDeleteDialogOpen(false);
			// If we're viewing this bookmark, navigate away
			if (isActive) {
				navigate(`/admin/content/${bookmark.collection}`);
			}
		} catch (error) {
			toast.error('Failed to delete bookmark');
		} finally {
			setSaving(false);
		}
	};

	const openEditDialog = () => {
		setEditName(bookmark.bookmark || '');
		setEditIcon(bookmark.icon || 'bookmark');
		setEditColor(bookmark.color || '');
		setEditDialogOpen(true);
	};

	return (
		<>
			<div
				className={cn(
					'group flex items-center gap-1 rounded-md text-sm transition-colors',
					isActive
						? 'bg-primary/80 text-primary-foreground'
						: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
				)}
				style={{ paddingLeft: `${8 + level * 12}px` }}
			>
				<Link
					to={`/admin/content/${bookmark.collection}?bookmark=${bookmark.id}`}
					className="flex flex-1 items-center gap-2 py-1.5 pr-1"
				>
					<Bookmark className="h-4 w-4 shrink-0" style={color ? { color } : undefined} />
					<span className="truncate">{bookmark.bookmark || 'Untitled Bookmark'}</span>
				</Link>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="sm"
							className={cn(
								'h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity',
								isActive && 'text-primary-foreground hover:text-primary-foreground'
							)}
							onClick={(e) => e.stopPropagation()}
						>
							<MoreVertical className="h-3 w-3" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-44">
						<DropdownMenuItem onClick={openEditDialog} disabled={!isOwner}>
							<Pencil className="mr-2 h-4 w-4" />
							{isOwner ? 'Edit' : 'Read-only'}
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							onClick={() => setDeleteDialogOpen(true)}
							className="text-destructive focus:text-destructive"
							disabled={!isOwner}
						>
							<Trash2 className="mr-2 h-4 w-4" />
							Delete
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{/* Edit Dialog */}
			<Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
				<DialogContent className="sm:max-w-[400px]">
					<DialogHeader>
						<DialogTitle>Edit Bookmark</DialogTitle>
						<DialogDescription>Update the bookmark name, icon, and color.</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label htmlFor="bookmark-name">Name</Label>
							<Input
								id="bookmark-name"
								value={editName}
								onChange={(e) => setEditName(e.target.value)}
								placeholder="Bookmark name"
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor="bookmark-icon">Icon</Label>
							<Input
								id="bookmark-icon"
								value={editIcon}
								onChange={(e) => setEditIcon(e.target.value)}
								placeholder="bookmark"
							/>
						</div>
						<div className="grid gap-2">
							<Label htmlFor="bookmark-color">Color</Label>
							<Input
								id="bookmark-color"
								type="color"
								value={editColor || '#6366f1'}
								onChange={(e) => setEditColor(e.target.value)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setEditDialogOpen(false)}>
							Cancel
						</Button>
						<Button onClick={handleEdit} disabled={saving || !editName.trim()}>
							{saving ? 'Saving...' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete Bookmark</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete "{bookmark.bookmark}"? This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDelete}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{saving ? 'Deleting...' : 'Delete'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

function CollectionItem({
	collection,
	isActive,
	search,
	showHidden,
	level = 0,
	children = [],
	bookmarks = [],
	activeBookmarkId,
	childrenMap,
	bookmarksMap,
	currentCollection,
}: CollectionItemProps) {
	const { isGroupExpanded, toggleGroup } = useContentStore();
	const isExpanded = isGroupExpanded(collection.collection);
	const hasChildren = children.length > 0;
	const isHidden = collection.meta?.hidden;

	// Filter children based on search
	const filteredChildren = useMemo(() => {
		if (!search) return children;
		return children.filter(
			(c) =>
				c.collection.toLowerCase().includes(search.toLowerCase()) ||
				(c.meta?.collection || c.collection).toLowerCase().includes(search.toLowerCase())
		);
	}, [children, search]);

	// Don't show hidden collections unless showHidden is true
	if (isHidden && !showHidden) return null;

	// Format collection name (capitalize and replace underscores with spaces)
	const formatName = (name: string) => {
		return name
			.split('_')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	// For folders (collections without schema), show as collapsible group
	if (!collection.schema && hasChildren) {
		return (
			<Collapsible open={isExpanded} onOpenChange={() => toggleGroup(collection.collection)}>
				<CollapsibleTrigger asChild>
					<button
						className={cn(
							'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
							'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
							isHidden && 'opacity-50'
						)}
						style={{ paddingLeft: `${8 + level * 12}px` }}
					>
						<ChevronRight className={cn('h-4 w-4 shrink-0 transition-transform', isExpanded && 'rotate-90')} />
						{isExpanded ? (
							<FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
						) : (
							<Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
						)}
						<span className="truncate">{formatName(collection.meta?.collection || collection.collection)}</span>
					</button>
				</CollapsibleTrigger>
				<CollapsibleContent>
					{filteredChildren.map((child) => {
						const childIsFolder = !child.schema;
						const childChildren = childIsFolder && childrenMap ? childrenMap.get(child.collection) || [] : [];
						const childBookmarks = bookmarksMap ? bookmarksMap.get(child.collection) || [] : [];

						return (
							<CollectionItem
								key={child.collection}
								collection={child}
								isActive={child.collection === currentCollection}
								search={search}
								showHidden={showHidden}
								level={level + 1}
								children={childChildren}
								childrenMap={childrenMap}
								bookmarks={childBookmarks}
								activeBookmarkId={activeBookmarkId}
								bookmarksMap={bookmarksMap}
								currentCollection={currentCollection}
							/>
						);
					})}
				</CollapsibleContent>
			</Collapsible>
		);
	}

	// Regular collection - if it has bookmarks, show as expandable
	const color = collection.meta?.color;
	// Replace underscores with spaces for display, similar to field names
	const displayName = (collection.meta?.collection || collection.collection).replace(/_/g, ' ');
	const hasBookmarks = bookmarks.length > 0;

	if (hasBookmarks) {
		return (
			<Collapsible open={isExpanded} onOpenChange={() => toggleGroup(collection.collection)}>
				<div className="flex items-center">
					<CollapsibleTrigger asChild>
						<button
							className={cn('shrink-0 p-1 transition-colors rounded hover:bg-accent')}
							style={{ marginLeft: `${level * 12}px` }}
						>
							<ChevronRight
								className={cn('h-4 w-4 text-muted-foreground transition-transform', isExpanded && 'rotate-90')}
							/>
						</button>
					</CollapsibleTrigger>
					<Link
						to={`/admin/content/${collection.collection}`}
						className={cn(
							'flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
							isActive && !activeBookmarkId
								? 'bg-primary text-primary-foreground'
								: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
							isHidden && 'opacity-50'
						)}
					>
						<Database className="h-4 w-4 shrink-0" style={color ? { color } : undefined} />
						<span className="truncate">{displayName}</span>
					</Link>
				</div>
				<CollapsibleContent>
					{bookmarks.map((bookmark) => (
						<NavigationBookmark
							key={bookmark.id}
							bookmark={bookmark}
							isActive={activeBookmarkId === bookmark.id}
							level={level + 1}
						/>
					))}
				</CollapsibleContent>
			</Collapsible>
		);
	}

	return (
		<Link
			to={`/admin/content/${collection.collection}`}
			className={cn(
				'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
				isActive
					? 'bg-primary text-primary-foreground'
					: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
				isHidden && 'opacity-50'
			)}
			style={{ paddingLeft: `${8 + level * 12}px` }}
		>
			<Database className="h-4 w-4 shrink-0" style={color ? { color } : undefined} />
			<span className="truncate capitalize">{displayName}</span>
		</Link>
	);
}

export function CollectionNavigation({ collections, currentCollection, activeBookmarkId }: CollectionNavigationProps) {
	const [search, setSearch] = useState('');
	const [showHidden, setShowHidden] = useState(false);

	// Get bookmarks from presets store
	const { bookmarks: getBookmarks } = usePresetsStore();
	const allBookmarks = getBookmarks();

	// Create a map of bookmarks per collection
	const bookmarksMap = useMemo(() => {
		const map = new Map<string, Preset[]>();
		allBookmarks.forEach((bookmark) => {
			if (bookmark.collection) {
				const existing = map.get(bookmark.collection) || [];
				map.set(bookmark.collection, [...existing, bookmark]);
			}
		});
		return map;
	}, [allBookmarks]);

	// Organize collections into tree structure with recursive grouping
	const { rootCollections, childrenMap } = useMemo(() => {
		const childrenMap = new Map<string, Collection[]>();
		const allCollectionsByKey = new Map<string, Collection>();
		const rootCollections: Collection[] = [];

		// First pass: index all collections
		collections.forEach((collection) => {
			allCollectionsByKey.set(collection.collection, collection);
		});

		// Second pass: build parent-child relationships recursively
		collections.forEach((collection) => {
			const group = collection.meta?.group;
			if (group) {
				// This collection belongs to a parent (folder or collection)
				const existing = childrenMap.get(group) || [];
				childrenMap.set(group, [...existing, collection]);
			} else {
				// No parent - this is a root collection
				rootCollections.push(collection);
			}
		});

		// Sort collections by sort field
		const sortFn = (a: Collection, b: Collection) => {
			const aSort = a.meta?.sort ?? 999;
			const bSort = b.meta?.sort ?? 999;
			if (aSort !== bSort) return aSort - bSort;
			return a.collection.localeCompare(b.collection);
		};

		rootCollections.sort(sortFn);
		childrenMap.forEach((children, key) => {
			childrenMap.set(key, children.sort(sortFn));
		});

		return { rootCollections, childrenMap };
	}, [collections]);

	// Filter root collections based on search
	const filteredRootCollections = useMemo(() => {
		if (!search) return rootCollections;

		// Include collection if it matches or any of its children match
		return rootCollections.filter((c) => {
			const matchesSelf =
				c.collection.toLowerCase().includes(search.toLowerCase()) ||
				(c.meta?.collection || c.collection).toLowerCase().includes(search.toLowerCase());

			const children = childrenMap.get(c.collection) || [];
			const hasMatchingChild = children.some(
				(child) =>
					child.collection.toLowerCase().includes(search.toLowerCase()) ||
					(child.meta?.collection || child.collection).toLowerCase().includes(search.toLowerCase())
			);

			return matchesSelf || hasMatchingChild;
		});
	}, [rootCollections, childrenMap, search]);

	// Check if there are hidden collections
	const hasHiddenCollections = collections.some((c) => c.meta?.hidden);

	// Show search if more than 20 collections
	const showSearch = collections.length > 20;

	return (
		<div className="flex h-full w-60 flex-col border-r bg-card overflow-hidden">
			{/* Search */}
			{showSearch && (
				<div className="border-b p-3">
					<div className="relative">
						<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder="Search collections..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-8"
						/>
					</div>
				</div>
			)}

			{/* Collections List */}
			<ScrollArea className="flex-1 min-h-0">
				<div className="p-2 space-y-0.5">
					{filteredRootCollections.length === 0 ? (
						<div className="px-2 py-8 text-center">
							<Database className="mx-auto h-8 w-8 text-muted-foreground/50" />
							<p className="mt-2 text-sm text-muted-foreground">
								{search ? 'No collections found' : 'No collections yet'}
							</p>
						</div>
					) : (
						filteredRootCollections.map((collection) => (
							<CollectionItem
								key={collection.collection}
								collection={collection}
								isActive={currentCollection === collection.collection}
								search={search}
								showHidden={showHidden}
								children={childrenMap.get(collection.collection) || []}
								bookmarks={bookmarksMap.get(collection.collection) || []}
								activeBookmarkId={activeBookmarkId}
								childrenMap={childrenMap}
								bookmarksMap={bookmarksMap}
								currentCollection={currentCollection}
							/>
						))
					)}
				</div>
			</ScrollArea>

			{/* Footer with show hidden toggle */}
			{hasHiddenCollections && (
				<div className="border-t p-2">
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="sm"
									className="w-full justify-start"
									onClick={() => setShowHidden(!showHidden)}
								>
									{showHidden ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
									{showHidden ? 'Hide Hidden' : 'Show Hidden'}
								</Button>
							</TooltipTrigger>
							<TooltipContent>{showHidden ? 'Hide hidden collections' : 'Show hidden collections'}</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
			)}
		</div>
	);
}
